import React from "react";
import { Handle, Position, type NodeProps } from "reactflow";
import { FiEye, FiHardDrive, FiDatabase } from "react-icons/fi";

import { SchemaTableName } from "./SchemaTableName";
import type { SatelliteNodeData } from "./types";
import { isVirtualizedConstruct } from "../model/constructs";
import { isInternalRegisteredAsset } from "./internalAssetStyling";
import { NotGeneratedBadge, notGeneratedNodeClasses } from "./notGeneratedStyling";

export function SatelliteNode({ data, selected }: NodeProps<SatelliteNodeData>): JSX.Element {
  const { entity, onSelect } = data;
  const isOrphan = !entity.parentHubId;
  const constructType = entity.record.datavault_construct_type ?? "";
  const isVirtualized = isVirtualizedConstruct(constructType);
  const isInternal = isInternalRegisteredAsset(entity.record.data_source_type);
  const StatusIcon = isInternal ? FiDatabase : isVirtualized ? FiEye : FiHardDrive;
  const statusLabel = isInternal ? "Internal" : isVirtualized ? "Virtualized" : "Persisted";
  const headerAccent = isOrphan ? "tw-text-amber-600" : "tw-text-emerald-600";

  const baseClasses =
    "tw-group tw-relative tw-min-w-[240px] tw-cursor-grab tw-rounded-3xl tw-border tw-bg-gradient-to-br tw-from-white tw-via-amber-50 tw-to-amber-100 tw-p-4 tw-text-left tw-shadow-lg tw-transition-all tw-duration-150 hover:tw--translate-y-1";
  const selectionClasses = selected
    ? "tw-border-[4px] tw-border-amber-700 tw-ring-[5px] tw-ring-amber-400/70 tw-shadow-[0_0_20px_rgba(146,64,14,0.45)]"
    : "tw-border-amber-200 tw-ring-1 tw-ring-amber-200/60";
  return (
    <div
      onClick={(event) => {
        if (event.shiftKey || event.metaKey || event.ctrlKey) {
          return;
        }
        onSelect(entity.id);
      }}
      className={`${baseClasses} ${selectionClasses} ${notGeneratedNodeClasses(entity.record)}`}
    >
      <NotGeneratedBadge record={entity.record} />
      <Handle
        type="target"
        id="sat-target-left"
        position={Position.Left}
        className="!tw-h-3 !tw-w-3 !tw-border !tw-border-amber-300 !tw-bg-white"
      />
      <Handle
        type="target"
        id="sat-target-right"
        position={Position.Right}
        className="!tw-h-3 !tw-w-3 !tw-border !tw-border-amber-300 !tw-bg-white"
      />
      <div className="tw-absolute tw-right-4 tw-top-4 tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-border tw-border-white/60 tw-bg-white/90 tw-px-2 tw-py-1 tw-text-[10px] tw-font-semibold tw-text-slate-600">
        <StatusIcon className={`tw-h-3 tw-w-3 ${isInternal || isVirtualized ? "tw-text-[#03234a]" : ""}`} />
        <span className={isInternal || isVirtualized ? "tw-text-[#03234a]" : "tw-text-slate-500"}>
          {statusLabel}
        </span>
      </div>
      <div className="tw-flex tw-items-center tw-gap-3">
        <div
          className="tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-2xl tw-bg-amber-500/90 tw-text-white tw-shadow-lg"
        >
          <span className="tw-text-base tw-font-semibold">S</span>
        </div>
        <div>
          <p className="tw-text-sm tw-font-semibold tw-text-slate-800">
            {isOrphan ? "Orphan Satellite" : "Satellite"}
          </p>
          <p className={`tw-text-[11px] tw-uppercase tw-tracking-wide ${headerAccent}`}>
            Descriptive attributes
          </p>
        </div>
      </div>
      <div className="tw-mt-3">
        <SchemaTableName
          schema={entity.record.target_schema}
          table={entity.record.target_table}
          className="tw-block tw-font-mono tw-text-[13px] tw-font-semibold tw-leading-5 tw-text-slate-900 tw-break-words"
        />
      </div>
    </div>
  );
}
